import React, { useEffect, useRef, useState } from 'react';
import { API_BASE_URL, API_ENDPOINTS } from '../../config/api.config';

const COUNT_OFFSET = 50;
const COUNT_DURATION_MS = 5500;
const COUNT_STAGGER_MS = 450;

const formatNumber = (num) => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const getTargetValue = (stat) => Number(stat.value ?? stat.count ?? 0) || 0;

const getStartValue = (end) => Math.max(0, end - COUNT_OFFSET);

const buildFallbackStats = (data) => [
  { key: 'properties', value: data.total_properties || 0, label: 'Properties Listed', order: 1 },
  { key: 'cities', value: data.total_cities || 0, label: 'Cities Covered', order: 2 },
  { key: 'users', value: data.total_users || 0, label: 'Happy Customers', order: 3 },
  { key: 'agents', value: data.total_agents || 0, label: 'Verified Agents', order: 4 },
];

const AchievementsSection = () => {
  const sectionRef = useRef(null);
  const hasAnimatedRef = useRef(false);
  const timersRef = useRef([]);

  const [statsConfig, setStatsConfig] = useState([]);
  const [displayStats, setDisplayStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [isActive, setIsActive] = useState(false);
  const [isCounting, setIsCounting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PUBLIC_STATS}`);
        const data = await response.json();

        if (cancelled) return;

        if (data.success && data.data) {
          let stats = [];
          if (data.data.stats && Array.isArray(data.data.stats)) {
            stats = [...data.data.stats].sort((a, b) => (a.order || 0) - (b.order || 0));
          } else {
            stats = buildFallbackStats(data.data);
          }

          const initialDisplay = {};
          stats.forEach((stat) => {
            initialDisplay[stat.key] = getStartValue(getTargetValue(stat));
          });

          setStatsConfig(stats);
          setDisplayStats(initialDisplay);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
        if (!cancelled) setStatsConfig([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchStats();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) {
      setIsActive(true);
      return undefined;
    }

    let done = false;
    const activate = () => {
      if (done) return;
      done = true;
      setIsActive(true);
    };

    const isInView = () => {
      const rect = section.getBoundingClientRect();
      return rect.top < window.innerHeight * 0.85 && rect.bottom > 0;
    };

    if (isInView()) {
      activate();
      return undefined;
    }

    const fallback = window.setTimeout(activate, 700);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) activate();
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -20px 0px' }
    );

    observer.observe(section);

    const onScroll = () => {
      if (isInView()) {
        activate();
        observer.disconnect();
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.clearTimeout(fallback);
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    if (!isActive || loading || statsConfig.length === 0 || hasAnimatedRef.current) {
      return undefined;
    }

    hasAnimatedRef.current = true;

    const clearTimers = () => {
      timersRef.current.forEach((id) => {
        window.clearTimeout(id);
        cancelAnimationFrame(id);
      });
      timersRef.current = [];
    };

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      const finalStats = {};
      statsConfig.forEach((stat) => {
        finalStats[stat.key] = getTargetValue(stat);
      });
      setDisplayStats(finalStats);
      return undefined;
    }

    setIsCounting(true);

    const startValues = {};
    const endValues = {};
    statsConfig.forEach((stat) => {
      const end = getTargetValue(stat);
      startValues[stat.key] = getStartValue(end);
      endValues[stat.key] = end;
    });

    setDisplayStats(startValues);

    statsConfig.forEach((stat, index) => {
      const key = stat.key;
      const start = startValues[key];
      const end = endValues[key];

      const staggerTimer = window.setTimeout(() => {
        if (start === end) {
          setDisplayStats((prev) => ({ ...prev, [key]: end }));
          return;
        }

        const begin = performance.now();

        const step = (now) => {
          const elapsed = now - begin;
          const progress = Math.min(elapsed / COUNT_DURATION_MS, 1);
          const current = progress >= 1 ? end : Math.round(start + (end - start) * progress);

          setDisplayStats((prev) => ({ ...prev, [key]: current }));

          if (progress < 1) {
            const frame = requestAnimationFrame(step);
            timersRef.current.push(frame);
          }
        };

        const frame = requestAnimationFrame(step);
        timersRef.current.push(frame);
      }, index * COUNT_STAGGER_MS);

      timersRef.current.push(staggerTimer);
    });

    return clearTimers;
  }, [isActive, loading, statsConfig]);

  return (
    <section
      ref={sectionRef}
      className={`about-section about-stats${isActive ? ' is-active' : ''}${isCounting ? ' is-counting' : ''}`}
      aria-labelledby="achievements-heading"
    >
      <div className="about-container">
        <div className="about-section-header">
          <h2 id="achievements-heading" className="about-section-title">
            Our Achievements
          </h2>
          <div className="about-title-underline" aria-hidden="true" />
        </div>

        <div className="stats-grid">
          {loading ? (
            <div className="stat-card">
              <div className="stat-number">...</div>
              <div className="stat-label">Loading...</div>
            </div>
          ) : statsConfig.length > 0 ? (
            statsConfig.map((stat, index) => {
              const end = getTargetValue(stat);
              const shown = displayStats[stat.key] ?? getStartValue(end);

              return (
                <div
                  key={stat.key}
                  className="stat-card"
                  style={{ '--stat-delay': `${index * 0.12}s` }}
                >
                  <div className="stat-number" data-animate="true">
                    {formatNumber(shown)}+
                  </div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              );
            })
          ) : (
            <div className="stat-card">
              <div className="stat-number">0+</div>
              <div className="stat-label">No data available</div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AchievementsSection;
